import { useRef, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/output.css";
import Button from "../components/basicComponents/button";
import TextInput from "../components/basicComponents/text-input";
import personIcon from "../assets/SVGs/person.svg";
import passIcon from "../assets/SVGs/pass.svg";
import eyeIcon from "../assets/SVGs/eye.svg";
import eyeIconOff from "../assets/SVGs/eye-off.svg";
import { userLogin } from "../state-management/actions/auth-actions";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../state-management/store"; // Import RootState type
import theme from "../config/theme";
import * as Icons from "../assets/SVGs/index";
import ToggleChangeTheme from "../components/toggle-change-theme"; // Adjust the path as necessary
import {
  isLogin,
  setPersistPassword,
  setPersistUsername,
  setRememberMe,
  setCurrentUserData,
} from "../state-management/actions/global-state-actions";
import Cookies from "js-cookie";
import Checkbox from "../components/checkbox";
import {
  setAlert,
  setAlertSeverity,
  setAlertContext,
} from "../state-management/actions/alerts-actions";
import { selectGlobalState } from "../state-management/selectors";
import { ReactComponent as LoginSvg } from "../assets/SVGs/login-svg.svg";
import { getCurrentUserInfo } from "../state-management/actions/user-actions";
import { loginSchema } from "../utils/validation-schema";
import { useFormik } from "formik";

const LoginPage = () => {
  const usernameRef = useRef<any>(null);
  const passwordRef = useRef<any>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { rememberMe, persistUsername, persistPassword } =
    useSelector(selectGlobalState);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const themeMode = useSelector((state: RootState) => state.globalState.theme);
  const mode = themeMode === "light" ? theme.light : theme.dark;

  useEffect(() => {
    if (rememberMe && persistUsername) {
      usernameRef.current.setValue(persistUsername);
    }
    if (rememberMe && persistPassword) {
      passwordRef.current.setValue(persistPassword);
    }
  }, [rememberMe, persistUsername, persistPassword]);

  const handleSubmit = async (values: {
    username: string;
    password: string;
  }) => {
    try {
      const data = { username: values.username, password: values.password };
      const response = await userLogin(data)(dispatch);
      Cookies.set("token", response.data.token, { expires: 7 });
      Cookies.set("refreshToken", response.data.refreshToken, { expires: 7 });
      dispatch(isLogin(true));
      dispatch(setAlert(true));
      dispatch(setAlertSeverity("success"));
      dispatch(setAlertContext("ورود موفقیت آمیز"));

      const userData = await getCurrentUserInfo()(dispatch);
      if (userData.status === 200) {
        dispatch(setCurrentUserData(userData.data));
      }
      if (rememberMe) {
        dispatch(setPersistUsername(data.username));
        dispatch(setPersistPassword(data.password));
      } else {
        dispatch(setPersistUsername(""));
        dispatch(setPersistPassword(""));
      }
    } catch (error: any) {
      dispatch(setAlert(true));
      dispatch(setAlertSeverity("error"));
      dispatch(
        setAlertContext(`نام کاربری و یا رمز عبور صحیح نمی باشد ${error}`)
      );
      console.error(error.response.data.error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRememberMe = (checked: boolean) => {
    dispatch(setRememberMe(checked));
  };

  const formik = useFormik({
    initialValues: {
      username: persistUsername || "",
      password: persistPassword || "",
    },
    validationSchema: loginSchema,
    onSubmit: handleSubmit,
  });

  return (
    <form
      className="w-full"
      style={{ backgroundColor: mode.backgroundColor.bgPrimary }}
      onSubmit={formik.handleSubmit}
    >
      <div className="w-full min-h-screen flex flex-col items-center justify-center">
        <div
          style={{ backgroundColor: mode.backgroundColor.bgForm }}
          className="grid md:grid-cols-2 items-center max-w-4xl max-md:max-w-lg w-full m-4 shadow-[0_2px_10px_-3px_rgba(128,128,128,0.3)] rounded-lg border border-[#0e9f6e3d]"
        >
          <div
            className="md:h-full rounded-lg p-8 flex justify-center items-center hidden md:flex"
            style={{ backgroundColor: mode.backgroundColor.bgGray }}
          >
            <LoginSvg style={{ color: "green" }} />
          </div>
          <div className="md:max-w-full w-full md:p-14 p-5">
            <div className="p-2 flex flex-col items-center justify-center">
              <p
                className="text-xl font-bold my-2"
                style={{ color: mode.color.textPrimary }}
              >
                وارد شوید!
              </p>
            </div>
            <TextInput
              ref={usernameRef}
              name="username"
              label="نام کاربری"
              value={formik.values.username}
              placeholder="نام کاربری خود را وارد کنید"
              RightIcon={<img src={personIcon} alt="person" />}
              textAlign="left"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              helperText={
                formik.touched.username && formik.errors.username
                  ? formik.errors.username
                  : undefined
              }
            />

            <TextInput
              ref={passwordRef}
              name="password"
              value={formik.values.password}
              label="رمز عبور"
              placeholder="رمز عبور خود را وارد کنید"
              RightIcon={<img src={passIcon} alt="password" />}
              LeftIcon={
                <img src={showPassword ? eyeIconOff : eyeIcon} alt="eye" />
              }
              secureTextEntry={!showPassword}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              onSubmit={() => buttonRef.current?.focus()}
              leftIconOnSubmit={() => setShowPassword(!showPassword)}
              helperText={
                formik.touched.password && formik.errors.password
                  ? formik.errors.password
                  : undefined
              }
            />
            <div
              className="flex justify-between mb-3 w-full flex-col md:flex-row text-xs"
              style={{ color: mode.color.textPrimary }}
            >
              <Checkbox
                label="من را به خاطر بسپار"
                value={rememberMe}
                onChangeCheckbox={handleRememberMe}
              />
              <p
                className="w-full text-left cursor-pointer"
                onClick={() => navigate("/forgot-password")}
                style={{ color: mode.color.textDanger }}
              >
                رمز عبورم را فراموش کردم!
              </p>
            </div>
            <Button
              ref={buttonRef}
              title="ورود"
              type="submit"
              color="white"
              backgroundColor="#2D667F"
              disabled={submitting}
              paddingx="153px"
              paddingy="10px"
            />
            {/* <div className="flex justify-start mt-2 w-full text-xs">
              <p
                className="text-right pl-2"
                style={{ color: mode.color.textPrimary }}
              >
                حساب کاربری ندارید!
              </p>
              <p
                className="border-r-2 border-mobasher-gray text-right pr-2 cursor-pointer"
                onClick={() => navigate("/register-page")}
                style={{ color: mode.color.textSuccess }}
              >
                ایجاد حساب کاربری
              </p>
            </div> */}
          </div>
        </div>
      </div>
      <div className="w-full justify-center pb-2">
     <div className="flex justify-around mt-4">
                <ToggleChangeTheme
                    lightIcon={
                        <Icons.MoonIcon
                            fill="#000000"
                            className="absolute bottom-[6%] left-[0%] h-6 w-6 rounded-full bg-[#ffffff] transition-all duration-500 peer-checked:transform peer-checked:translate-x-full"
                        />
                    }
                    darkIcon={
                        <Icons.SunIcon
                            fill="#ffffff"
                            className="absolute bottom-[6%] left-[-20%] h-6 w-6 rounded-full bg-[#25282D] transition-all duration-500 peer-checked:transform peer-checked:translate-x-full"
                        />
                    }
                />
            </div>
     </div>
    </form>
  );
};

export default LoginPage;
